<template>
  <a-select
    v-bind="selectAttrs"
    allowClear
    showSearch
    optionFilterProp="label"
    :value="internalValue"
    :mode="multiple ? 'multiple' : mode"
    @change="handleChange"
    style="width: 150px"
  >
    <a-select-option
      v-for="option in modifiedOptions"
      :key="option.value"
      :value="option.value"
      :label="option.label"
    >
      <a-checkbox
        v-if="multiple"
        :checked="isOptionSelected(option.value)"
        @click.prevent
      />
      {{ option.label }}
    </a-select-option>
  </a-select>
</template>

<script>
import {
  defineComponent,
  inject,
  reactive,
  toRefs,
  computed,
  onMounted,
  watch,
} from 'vue'

export default defineComponent({
  name: 'CustomSelect',
  props: {
    modelValue: {
      type: [String, Number, Object, Boolean, Array, null],
      default: null,
    },
    options: {
      type: Array,
      required: true,
    },
    hasCheckAll: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'combobox',
    },
    // 多選模式：搭配 hasCheckAll 時，「全部」跟其他選項互斥，
    // 三個具體選項都勾選時自動收合成「全部」
    multiple: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit, attrs }) {
    const $t = inject('$t')

    const state = reactive({
      internalValue: props.modelValue,
      modifiedOptions: computed(() => {
        if (props.hasCheckAll && props.options) {
          return [{ label: $t('checkAll'), value: null }, ...props.options]
        }
        return props.options || []
      }),
    })

    const defaultMultipleValue = () => (props.hasCheckAll ? [null] : [])

    watch(
      () => props.modelValue,
      (val) => {
        if (props.multiple && (!Array.isArray(val) || val.length === 0)) {
          state.internalValue = defaultMultipleValue()
          emit('update:modelValue', state.internalValue)
          return
        }
        state.internalValue = val
      }
    )

    const selectAttrs = computed(() => {
      // eslint-disable-next-line no-unused-vars
      const { onChange, ...restAttrs } = attrs
      return restAttrs
    })

    const isOptionSelected = (value) => {
      return Array.isArray(state.internalValue) && state.internalValue.includes(value)
    }

    const handleChange = (val) => {
      if (!props.multiple) {
        state.internalValue = val
        emit('update:modelValue', val)
        return
      }
      let newVal = Array.isArray(val) ? [...val] : []
      const hadAll = Array.isArray(state.internalValue) && state.internalValue.includes(null)
      const hasAll = newVal.includes(null)
      if (hasAll && !hadAll) {
        // 剛勾選「全部」：取消其他勾選
        newVal = [null]
      } else if (hasAll && hadAll && newVal.length > 1) {
        // 「全部」已存在，又勾了具體選項：取消「全部」
        newVal = newVal.filter((v) => v !== null)
      }
      const specificValues = (props.options || []).map((option) => option.value)
      const specificSelected = newVal.filter((v) => specificValues.includes(v))
      if (specificValues.length > 0 && specificSelected.length === specificValues.length) {
        // 具體選項全部勾選：收合成「全部」
        newVal = [null]
      }
      if (newVal.length === 0) {
        newVal = defaultMultipleValue()
      }
      state.internalValue = newVal
      emit('update:modelValue', newVal)
    }

    onMounted(() => {
      if (props.multiple) {
        if (!Array.isArray(state.internalValue) || state.internalValue.length === 0) {
          state.internalValue = defaultMultipleValue()
          emit('update:modelValue', state.internalValue)
        }
        return
      }
      if (state.internalValue === null && state.modifiedOptions.length > 0) {
        state.internalValue = props.hasCheckAll
          ? null
          : state.modifiedOptions[0].value
        emit('update:modelValue', state.internalValue)
      }
    })

    return {
      ...toRefs(state),
      selectAttrs,
      isOptionSelected,
      handleChange,
    }
  },
})
</script>
